import type { Option } from "../core/option.ts";
import { NaiveDate } from "@tento-chrono";
import type { CellCardinality } from "../core/dtype.ts";

/**
 * Cell-lane placement: the single authority that maps the TWO orthogonal cell
 * axes onto a storage lane. Both `RowData` (lona-js) and `RowCellData` (sheets)
 * route through this so the two substrates agree, and the Stage-2 lane-rows
 * migration (`:events.allday` / `:events.partial`) changes only this function.
 *
 * The axes (previously conflated — see the events-spine "deconfliation"):
 * - **identity / cardinality** — `single` (one cell per temporal key) vs
 *   `multi` (many cells per key, distinguished by id).
 * - **temporal key** — `semantic` (a date/ymd bucket) vs `timepoint` (a precise
 *   datetime). Orthogonal to cardinality: a date-keyed cell can still be
 *   id-distinguished (an all-day event; many per day).
 *
 * The lanes (matching the sheets `RowCellData` three-store model):
 * - `index`  — semantic + single (the date-keyed `SortedDeque`).
 * - `multi`  — semantic + multi  (date-keyed but id-distinguished).
 * - `custom` — timepoint (datetime-keyed by id; any cardinality).
 */
export type CellLane = "index" | "multi" | "custom";

export type TemporalKind = "semantic" | "timepoint";

export namespace CellLane {
  /** Map (cardinality, temporal-kind) → lane. Timepoint cells are always
   *  id-keyed in the custom store; semantic cells split by cardinality. */
  export function of(
    cardinality: CellCardinality,
    temporal: TemporalKind,
  ): CellLane {
    if (temporal === "timepoint") return "custom";
    return cardinality === "multi" ? "multi" : "index";
  }

  /**
   * Temporal kind inferred from a wire cell's `date` string shape: a
   * `NaiveDate.Partial` (ymd-/ym-/yw-/y-) is semantic; anything else (an
   * RFC3339 instant) is a timepoint. Mirrors `SemanticTime.parse`'s
   * partial-first probe. For an event cell prefer the value-authoritative
   * {@link temporalKindOfEvent} — an all-day event whose wire `date` is an
   * rfc3339 midnight would mis-infer `timepoint` here.
   */
  export function temporalKindOf(wireDate: string): TemporalKind {
    return NaiveDate.Partial.parseOptional(wireDate) != null ? "semantic" : "timepoint";
  }

  /**
   * Value-authoritative temporal kind for an event: an all-day event carries
   * `startDate`/`endDate` and is `semantic` regardless of how its wire `date`
   * string was shaped; a timed event is `timepoint`. This is the override that
   * keeps an all-day event out of the datetime lane even if its wire cell key
   * arrives as an rfc3339 midnight.
   */
  export function temporalKindOfEvent(
    ev: { readonly startDate: Option<NaiveDate>; readonly endDate: Option<NaiveDate> },
  ): TemporalKind {
    return ev.startDate != null && ev.endDate != null ? "semantic" : "timepoint";
  }
}
